/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useTable, useBlockLayout, HeaderGroup } from 'react-table';
import { FixedSizeList } from 'react-window';
import { Color } from '@amcharts/amcharts4/core';
import { XYSeries } from '@amcharts/amcharts4/charts';

import { getScrollbarWidth } from '@trqk/utils';

import {
    DollarsCellStandard,
    TableHeader,
    TableHeaderGroup,
    TableRow,
} from '../../../..';
import { legendSizes } from '../constants';
import { EntityNameCell, QuarterlyRunRateLegendSummary } from '.';

const { defaultColumnWidth, legendSummaryHeight, rowHeight } = legendSizes;

interface QuarterlyRunRateLegendProps {
    height: number;
    items: QRRLegendTableItem[];
    refresh: number;
    /**
     * The subscription_royalty_capacity in the user's current payment plan
     */
    royaltyCapacity: Maybe<number>;
    /**
     * When true, the word "Unlimited" will be used as the label for royalty capacity in the legend
     * summary.
     */
    unlimitedRoyalties: boolean;
}

/**
 * A legend for the QuarterlyRunRate chart, showing a table with a row for each entity which shows
 * the sum of all statement earnings for that entity as well as the earnings in the most recent
 * statement. Renders a {@link QuarterlyRunRateLegendSummary} below the table to show summary info.
 * @param props - {@link QuarterlyRunRateLegendProps}
 */
export const QuarterlyRunRateLegend = ({
    height,
    items,
    refresh,
    royaltyCapacity,
    unlimitedRoyalties,
}: QuarterlyRunRateLegendProps): JSX.Element => {
    const [data, setData] = useState(items);

    useEffect(() => {
        if (refresh > 0) {
            setData(items);
        }
    }, [refresh]);

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: 'entityName',
                accessor: ({
                    color,
                    entityName,
                    series,
                }: {
                    color: Color | null;
                    entityName: string;
                    series: XYSeries;
                }) => ({
                    color,
                    entityName,
                    series,
                }),
                Cell: EntityNameCell,
                headerLabel: 'Entity',
                Header: TableHeader,
                disableSortBy: true,
                width: legendSizes.columnWidths.entityName,
            },
            {
                id: 'totalEarnings',
                accessor: ({ totalEarnings }: QRRLegendTableItem): number =>
                    totalEarnings,
                Cell: DollarsCellStandard,
                headerLabel: 'All',
                Header: TableHeader,
                disableSortBy: true,
                width: legendSizes.columnWidths.totalEarnings,
            },
            {
                id: 'mostRecentEarnings',
                accessor: ({
                    mostRecentEarnings,
                }: QRRLegendTableItem): number => mostRecentEarnings,
                Cell: DollarsCellStandard,
                headerLabel: 'Most Recent',
                Header: TableHeader,
                disableSortBy: true,
                width: legendSizes.columnWidths.mostRecentEarnings,
            },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
    } = useTable<QRRLegendTableItem>(
        {
            columns,
            data,
            defaultColumn,
        },
        useBlockLayout,
    );

    /* END table management logic */

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            return (
                <TableRow<QRRLegendTableItem>
                    index={index}
                    style={style}
                    row={row}
                />
            );
        },
        [prepareRow, rows],
    );

    // The table has a calculated height so that the QuarterlyRunRateLegendSummary will always
    // fit beneath it.
    const listHeight = Math.min(
        data.length * rowHeight,
        height - legendSummaryHeight,
    );

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <div>
            <div
                {...getTableProps()}
                className="trqk-table text-sm font-normal border-b border-tertiary-400 "
            >
                <div>
                    {headerGroups.map(
                        (
                            headerGroup: HeaderGroup<QRRLegendTableItem>,
                        ): JSX.Element => {
                            const { key: headerGroupKey } =
                                headerGroup.getHeaderGroupProps();

                            return (
                                <TableHeaderGroup<QRRLegendTableItem>
                                    key={headerGroupKey}
                                    headerGroup={headerGroup}
                                />
                            );
                        },
                    )}
                </div>
                <div {...getTableBodyProps()}>
                    <FixedSizeList
                        height={listHeight}
                        itemCount={rows.length}
                        itemSize={rowHeight}
                        width={totalColumnsWidth + scrollbarWidth}
                    >
                        {RenderRow}
                    </FixedSizeList>
                </div>
            </div>
            <QuarterlyRunRateLegendSummary
                data={data}
                royaltyCapacity={royaltyCapacity}
                unlimitedRoyalties={unlimitedRoyalties}
            />
        </div>
    );
};
